import React, { useEffect, useState } from "react"
import { Box, CircularProgress, Grid, Typography } from "@mui/material"
import PageWrapper from "Pages/@PageWrapper"
import {
	AppButtonIcon, AppCardTitle, AppCrudTable, AppDialog, AppDialogBody, AppDialogHeader, AppEmptyLine, AppResultsSkeleton,
	AppToolbarCreateButton,
} from "@components"
import { dsColorNew } from "@palette"
import { AUTO_EUROPE_TOOL_ROUTES, BACKEND_APIS } from "@constants/routes"
import { getExcel } from "@ui-seeker/utils"

const SEASON_SETS_API = `${BACKEND_APIS.AUTOEUROPE_PRICING_TOOL}/season-sets`
import {
	autoEuropeSeasonSetsDrawerData,
	autoEuropeSeasonSetsIsLoading,
	autoEuropeSeasonSetsListData,
} from "Pages/AutoEuropeTool/SeasonSets/@signals"
import AutoEuropeSeasonSetsDrawer from "Pages/AutoEuropeTool/SeasonSets/Drawer"
import useAutoEuropeSeasonSetsTable from "Pages/AutoEuropeTool/SeasonSets/@hooks/useTable"
import useAutoEuropeSeasonSetsFormSubmit from "Pages/AutoEuropeTool/SeasonSets/@hooks/useFormSubmit"
import useAutoEuropeSeasonSetsActions from "Pages/AutoEuropeTool/SeasonSets/@hooks/useActions"
import useAutoEuropeSeasonSetsEdit from "Pages/AutoEuropeTool/SeasonSets/@hooks/useEdit"
import useAutoEuropeSeasonSetsClone from "Pages/AutoEuropeTool/SeasonSets/@hooks/useClone"

const EMPTY_SEASON_RULES = { dateRanges: [], followingDays: [], nextSaturday: [], finalSeason: false }
const EMPTY_FORM = { name: null, seasonRules: EMPTY_SEASON_RULES }

function rulesSummary(rules) {
	if (!rules) return '-'
	const parts = []
	if (rules.dateRanges?.length) parts.push(`${rules.dateRanges.length} date range${rules.dateRanges.length > 1 ? 's' : ''}`)
	if (rules.followingDays?.length) parts.push(rules.followingDays.map(d => `${d.from}–${d.to}d`).join(', '))
	if (Array.isArray(rules.nextSaturday) && rules.nextSaturday.length) parts.push(`${rules.nextSaturday.length} sat`)
	if (rules.finalSeason) parts.push('Final season')
	return parts.length ? parts.join(' · ') : '-'
}

const centered = v => <Box sx={{ display: 'flex', justifyContent: 'center' }}>{v}</Box>

function ReportsDialog({ seasonSetId, seasonSetName, onClose }) {
	const [tools, setTools] = useState(null)

	useEffect(() => {
		fetch(`${BACKEND_APIS.AUTOEUROPE_PRICING_TOOL}/list`)
			.then(r => r.json())
			.then(data => {
				const matching = (data || []).filter(t => t.seasonSetId === seasonSetId)
				setTools(matching)
			})
			.catch(() => setTools([]))
	}, [seasonSetId])

	const hasReports = tools?.some(t => t.reportsName?.length)

	return (
		<AppDialog open onClose={onClose} maxWidth="sm">
			<AppDialogHeader
				icon="file-lines"
				title={<>Reports using <strong>{seasonSetName}</strong></>}
				onClose={onClose}
			/>
			<AppDialogBody>
				{tools === null
					? <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24}/></Box>
					: !hasReports
						? <AppEmptyLine sx={{ py: 1 }}>No reports assigned to this season set.</AppEmptyLine>
						: <Box sx={{ border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '10px', maxHeight: 360, overflowY: 'auto' }}>
							{tools.map(tool => (
								tool.reportsName?.length ? (
									<Box key={tool.id} sx={{
										px: 1.75, py: 1.25, borderBottom: `1px solid ${dsColorNew.grayBorder}`,
										'&:last-of-type': { borderBottom: 0 },
									}}>
										<Typography sx={{ fontSize: 13, fontWeight: 600, color: dsColorNew.textTitle }}>{tool.name}</Typography>
										<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, mt: 0.25 }}>{tool.reportsName.join(', ')}</Typography>
									</Box>
								) : null
							))}
						</Box>
				}
			</AppDialogBody>
		</AppDialog>
	)
}

function ActionsCell({ row, onDelete }) {
	const { onEdit, isLoading: isEditLoading } = useAutoEuropeSeasonSetsEdit(row.id)
	const { onClone, isLoading: isCloneLoading } = useAutoEuropeSeasonSetsClone(row.id)
	const [showReports, setShowReports] = useState(false)
	return (
		<Box sx={{ display: 'flex', justifyContent: 'center' }}>
			{isEditLoading
				? <CircularProgress size={17} sx={{ m: 0.2 }}/>
				: <AppButtonIcon icon="pen" tooltip="Edit" onClick={e => { e.stopPropagation(); onEdit() }}/>
			}
			{isCloneLoading
				? <CircularProgress size={17} sx={{ m: 0.2 }}/>
				: <AppButtonIcon icon="clone" tooltip="Clone" onClick={e => { e.stopPropagation(); onClone() }}/>
			}
			<AppButtonIcon
				icon="file-lines"
				tooltip="Reports using this season set"
				onClick={e => { e.stopPropagation(); setShowReports(true) }}
			/>
			<AppButtonIcon
				icon="trash-can"
				tooltip="Delete"
				onClick={e => { e.stopPropagation(); onDelete(row.id, row.name) }}
			/>
			{showReports && (
				<ReportsDialog
					seasonSetId={row.id}
					seasonSetName={row.name}
					onClose={() => setShowReports(false)}
				/>
			)}
		</Box>
	)
}

export default function AutoEuropeSeasonSetsPage() {
	const { getTableData } = useAutoEuropeSeasonSetsTable()
	const { onSubmit } = useAutoEuropeSeasonSetsFormSubmit({ getTableData })
	const { onDelete } = useAutoEuropeSeasonSetsActions()

	useEffect(() => {
		getTableData()
	}, [])

	return <PageWrapper
		head={`DS Rentacar | ${AUTO_EUROPE_TOOL_ROUTES.SUBTITLE}`}
		title={AUTO_EUROPE_TOOL_ROUTES.SEASON_SETS_TITLE}
		subtitle={AUTO_EUROPE_TOOL_ROUTES.SEASON_SETS_SUBTITLE}
	>
		<AutoEuropeSeasonSetsDrawer onSubmit={onSubmit}/>
		<Grid container spacing={2}>
			<Grid item xs={12}>
				{autoEuropeSeasonSetsIsLoading.value
					? <AppResultsSkeleton rows={7} columns={3}/>
					: <AppCrudTable
						title={<AppCardTitle icon="calendar-days" title="Season Sets"/>}
						data={autoEuropeSeasonSetsListData.value}
						columns={[
							{
								name: 'name',
								label: 'Name',
								options: {
									filterType: 'textField',
									customBodyRowRender: v => centered(v),
								}
							},
							{
								name: 'seasonRules',
								label: 'Rules',
								options: {
									filter: false,
									sort: false,
									// without sorting the label prints bare and left: centred like its values
									setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
									customBodyRowRender: v => centered(rulesSummary(v)),
								}
							},
							{
								name: 'id',
								label: 'Actions',
								options: {
									filter: false,
									sort: false,
									setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
									customBodyRowRender: (_, row) => <ActionsCell row={row} onDelete={onDelete}/>,
								}
							},
						]}
						options={{
							customToolbar: () => <AppToolbarCreateButton
								label="Create"
								icon="file-plus"
								onClick={() => { autoEuropeSeasonSetsDrawerData.value = { ...EMPTY_FORM } }}
								/>,
							onDownload: () => getExcel(`${SEASON_SETS_API}/list/excel`, {}),
						}}
						rowActions={row => [
							{ label: 'Delete', icon: 'trash', onClick: () => onDelete(row.id, row.name) },
						]}
					/>
				}
			</Grid>
		</Grid>
	</PageWrapper>
}
